import { ReportStateV1, ReportPageSize, REPORT_PAGE_SIZES } from '../../api/deviceListReports';
import { FILTERABLE_BY_FILTER_KEY } from './columnRegistry';
import {
  DeviceListView,
  MAX_SAVED_WIDTH,
  MIN_SAVED_WIDTH,
  decodeView,
  defaultView,
} from './deviceListPrefs';
import { LEGACY_FILTER_ALIASES, FILTER_PARAM_PREFIX, filtersFromSearchParams } from './searchParamCodec';
import { own } from '../../utils/ownKey';

/**
 * FS-889 — the pure half of saved reports: the current view as the state a
 * report stores, the canonical form "Modified" compares, and the URL a report
 * installs. Nothing here touches React, storage or the network.
 */

const DEFAULT_PAGE_SIZE = 20;

/** The URL params that carry the data view (plus `report` / `rv`, FS-889). */
export const REPORT_PARAM = 'report';
/** "This URL carries the complete data view": present once a report's data is in the URL. */
export const REPORT_VIEW_PARAM = 'rv';

/** The report id in a URL, or null when absent or not a positive integer. */
export function reportIdFromParams(sp: URLSearchParams): number | null {
  const raw = sp.get(REPORT_PARAM);
  if (raw === null || !/^\d+$/.test(raw)) return null;
  const id = Number(raw);
  return id > 0 && Number.isSafeInteger(id) ? id : null;
}

export const hasReportViewMarker = (sp: URLSearchParams): boolean => sp.get(REPORT_VIEW_PARAM) === '1';

/**
 * True when the URL carries any data-view param (`f.*`, a legacy alias, `q`
 * or `sort`). The key's presence counts, not its value: `?report=5&q=` is a
 * link that deliberately opens the report with no search text, and the
 * report's own search text must not be put back.
 */
export function urlCarriesData(sp: URLSearchParams): boolean {
  let carries = false;
  sp.forEach((_v, k) => {
    if (k === 'q' || k === 'sort' || k === 'dir') carries = true;
    else if (k.startsWith(FILTER_PARAM_PREFIX) || LEGACY_FILTER_ALIASES[k] !== undefined) carries = true;
  });
  return carries;
}

/** A list item that is an integer id, as the facet lists it: no leading zero. */
const plainDecimalId = (item: string): string => (/^\d+$/.test(item) ? item.replace(/^0+(?=\d)/, '') : item);

/**
 * A filter value spelled the way the Device List's editors write it, which
 * is the only spelling the server stores: list filters (enum, prefix)
 * comma-joined with no spaces around items and no empty items, integer ids
 * (`intValues`: catalog filters, tags) in plain decimal; boolean, number and
 * date values without whitespace; text as typed. A view from an old bookmark
 * or a hand-edited URL (`f.firmware_version=0.08, 0.09`, `f.tags=007`)
 * searches fine today, because the search parser trims and reads the
 * number, but a report holding it would be refused on Save. An unknown key
 * is left as it is (the server names it in its refusal).
 *
 * Save sends this spelling and Modified compares it (`canonicalView`), so a
 * view that differs from the saved report only in spelling is not modified.
 */
export function canonicalFilterValue(key: string, value: string): string {
  const col = own(FILTERABLE_BY_FILTER_KEY, key);
  const type = col?.type;
  if (type === 'enum' || type === 'prefix') {
    return value
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s !== '')
      .map((s) => (col?.intValues ? plainDecimalId(s) : s))
      .join(',');
  }
  if (type === 'boolean') return value.trim();
  if (type === 'number' || type === 'date') return value.replace(/\s+/g, '');
  return value;
}

/** The current view as the state a report stores. Pending filter chips are not
 *  in `filters` (the URL never carries empty values), filter values are in
 *  their canonical spelling, every width is rounded and brought into the
 *  range a report may hold (a column dragged wider than that saves at the
 *  limit rather than failing the save or losing its width), and the page
 *  size falls back to the default. */
export function toReportState(view: DeviceListView): ReportStateV1 {
  const widths: Record<string, number> = {};
  for (const [k, v] of Object.entries(view.widths)) {
    if (!Number.isFinite(v)) continue;
    widths[k] = Math.min(MAX_SAVED_WIDTH, Math.max(MIN_SAVED_WIDTH, Math.round(v)));
  }
  const filters: Record<string, string> = {};
  for (const [k, v] of Object.entries(view.filters)) {
    const spelled = canonicalFilterValue(k, v);
    if (spelled !== '') filters[k] = spelled;
  }
  const pageSize = (REPORT_PAGE_SIZES as readonly number[]).includes(view.pageSize)
    ? (view.pageSize as ReportPageSize)
    : (DEFAULT_PAGE_SIZE as ReportPageSize);
  return {
    v: 1,
    columnOrder: [...view.columnOrder],
    hidden: [...view.hidden],
    widths,
    filters,
    sort: view.sort,
    pageSize,
    q: view.q,
  };
}

/** A filter whose value is a set of options (order carries no meaning). */
const isSetValued = (key: string): boolean => {
  const type = own(FILTERABLE_BY_FILTER_KEY, key)?.type;
  return type === 'enum' || type === 'prefix';
};

/**
 * The canonical form "Modified" compares: filter keys sorted, every value in
 * the spelling Save sends (`canonicalFilterValue`, so ` true ` from a
 * hand-edited URL is the saved `true`), and for a set-valued filter its items
 * sorted as well (a multiselect ticked in a different order is the same
 * filter); text, number and date values and the search text otherwise as
 * typed (a pasted id list's order matters); column order and sort levels in
 * order; hidden as a sorted set; page size. Widths are saved but never
 * compared — a one-pixel drag is not a change to the report — and pending
 * chips and filters whose canonical value is empty never count.
 */
export function canonicalView(view: DeviceListView): string {
  const filters: [string, string][] = [];
  for (const k of Object.keys(view.filters).sort()) {
    const spelled = canonicalFilterValue(k, view.filters[k]);
    if (spelled === '') continue;
    filters.push([k, isSetValued(k) ? spelled.split(',').sort().join(',') : spelled]);
  }
  return JSON.stringify({
    columnOrder: view.columnOrder,
    hidden: [...new Set(view.hidden)].sort(),
    filters,
    sort: view.sort,
    pageSize: view.pageSize,
    q: view.q,
  });
}

export const viewsDiffer = (a: DeviceListView, b: DeviceListView): boolean =>
  canonicalView(a) !== canonicalView(b);

/** A copy of plain JSON with every object's keys sorted, at any depth. */
const sortedKeys = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(sortedKeys);
  if (typeof value !== 'object' || value === null) return value;
  const obj = value as Record<string, unknown>;
  return Object.fromEntries(Object.keys(obj).sort().map((k) => [k, sortedKeys(obj[k])]));
};

/** A report state as one string: object keys in any order, hidden as a set. */
const stableState = (state: unknown): string => {
  const sorted = sortedKeys(state);
  if (typeof sorted === 'object' && sorted !== null && !Array.isArray(sorted)) {
    const s = sorted as Record<string, unknown>;
    if (Array.isArray(s.hidden)) s.hidden = [...s.hidden].sort();
  }
  return JSON.stringify(sorted);
};

/**
 * FS-924 — Save would store exactly what the report already holds:
 * `toReportState` of the view on screen (widths included, which Modified
 * ignores) against the saved state, compared in a stable form because the
 * server does not hand keys back in the order they were sent.
 */
export const sameReportState = (state: ReportStateV1, saved: unknown): boolean =>
  stableState(state) === stableState(saved);

/** The view a report's stored state decodes to (its layout merged with the registry). */
export const viewFromReportState = (state: unknown) => decodeView(state, 'report');

/** Every data-view param, legacy aliases and paging included. */
export function stripDataParams(sp: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(sp);
  const gone: string[] = [];
  next.forEach((_v, k) => {
    if (k.startsWith(FILTER_PARAM_PREFIX) || LEGACY_FILTER_ALIASES[k] !== undefined) gone.push(k);
  });
  for (const k of [...gone, 'q', 'sort', 'dir', 'page', 'page_size']) next.delete(k);
  return next;
}

/** Write a data view into the URL (after `stripDataParams`). */
function setDataParams(next: URLSearchParams, view: DeviceListView): void {
  for (const [k, v] of Object.entries(view.filters)) if (v !== '') next.set(`${FILTER_PARAM_PREFIX}${k}`, v);
  if (view.q) next.set('q', view.q);
  if (view.sort) next.set('sort', view.sort);
  if (view.pageSize !== DEFAULT_PAGE_SIZE) next.set('page_size', String(view.pageSize));
}

/**
 * The URL a report installs: every existing data param (`f.*`, legacy alias,
 * `q`, `sort`, `dir`, `page`, `page_size`) removed, the report's values set,
 * then `report=<id>` and `rv=1`. Unrelated params stay.
 *
 * `keepData`: a link that carried its own filters, search or sort keeps them
 * (only the report's layout is taken); the marker and id are still written.
 */
export function reportUrlParams(
  prev: URLSearchParams,
  view: DeviceListView,
  reportId: number,
  { keepData = false }: { keepData?: boolean } = {},
): URLSearchParams {
  let next: URLSearchParams;
  if (keepData) {
    next = new URLSearchParams(prev);
  } else {
    next = stripDataParams(prev);
    setDataParams(next, view);
  }
  next.set(REPORT_PARAM, String(reportId));
  next.set(REPORT_VIEW_PARAM, '1');
  return next;
}

/** The URL for the Default view: no data params, no report, no marker. */
export function defaultViewUrlParams(prev: URLSearchParams): URLSearchParams {
  const next = stripDataParams(prev);
  next.delete(REPORT_PARAM);
  next.delete(REPORT_VIEW_PARAM);
  return next;
}

/** The URL with the report closed; the data view it carries is kept. */
export function withoutReportParams(prev: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(prev);
  next.delete(REPORT_PARAM);
  next.delete(REPORT_VIEW_PARAM);
  return next;
}

/** The URL for an unsaved working view written back from prefs (no report). */
export function workingViewUrlParams(prev: URLSearchParams, view: DeviceListView): URLSearchParams {
  const next = stripDataParams(prev);
  setDataParams(next, view);
  return next;
}

/** The data half of the view the URL describes (layout comes from elsewhere). */
export function dataViewFromParams(sp: URLSearchParams, sort: string, pageSize: number): Pick<DeviceListView, 'filters' | 'sort' | 'pageSize' | 'q'> {
  return { filters: filtersFromSearchParams(sp), sort, pageSize, q: sp.get('q') ?? '' };
}

/** Default view: registry layout, no filters, no search, no sort, default page size. */
export const defaultDeviceListView = (): DeviceListView => defaultView();

/** One line per shared value, for the Share and Save-on-shared confirmations. */
export interface SharedContentLine {
  label: string;
  value: string;
}

/**
 * The exact content a report publishes: every active filter as its label and
 * raw value (a facet label, when the response carried one, follows the raw
 * value) and the search text. Taken from the candidate that will be
 * persisted, never from a view the operator may have moved away from.
 */
export function sharedContentLines(
  view: DeviceListView,
  facetLabel: (filterKey: string, value: string) => string | undefined = () => undefined,
): SharedContentLine[] {
  const lines: SharedContentLine[] = [];
  for (const k of Object.keys(view.filters).sort()) {
    const raw = view.filters[k];
    if (raw === '') continue;
    const col = own(FILTERABLE_BY_FILTER_KEY, k);
    const label = col?.label ?? k;
    const value = isSetValued(k)
      ? raw
          .split(',')
          .map((s) => s.trim())
          .filter((s) => s !== '')
          .map((v) => {
            const named = facetLabel(k, v);
            return named && named !== v ? `${v} (${named})` : v;
          })
          .join(', ')
      : raw;
    lines.push({ label, value });
  }
  if (view.q !== '') lines.push({ label: 'Search', value: view.q });
  return lines;
}

/** `^/([^/]+/)*$`, at most 255 characters, none of `%`, `_`, `\`. */
export const isScopePath = (path: string): boolean =>
  path.length <= 255 && /^\/([^/]+\/)*$/.test(path) && !/[%_\\]/.test(path);
